import React from 'react';
import { Box } from '@gluestack-ui/themed';

export type IdCaptureMode = 'document' | 'selfie';

export type IdCaptureGuideOverlaysProps = {
  mode: IdCaptureMode;
};

/**
 * Aspect ratio of the on-screen preview container (`IdVerificationStep` renders
 * the `<video>` inside a `4/3` box with `object-fit: cover`). The capture crop
 * math depends on this exact value, so it lives here next to the guide rects and
 * is imported by the capture code — never hard-code `4/3` in two places.
 */
export const ID_CAPTURE_PREVIEW_ASPECT = 4 / 3;

export type GuideRect = { x: number; y: number; width: number; height: number };

/**
 * The alignment outline the student sees AND the region the capture is cropped
 * to are the SAME fractional rectangle (fractions of the preview container,
 * 0..1). This is the single source of truth (system-contracts §11): the overlay
 * is drawn from these fractions and `captureStillFromVideo` crops to them, so
 * "put your face/card inside the outline" produces exactly that region in the
 * saved image — no drift between what is shown and what is captured.
 *
 * Heights are chosen so each outline's *on-screen* aspect matches the real
 * subject: the document rect renders at the ID-1 card ratio (~1.75) and the
 * selfie rect renders as a ~3:4 portrait, given the 4/3 container.
 */
export const ID_CAPTURE_GUIDE_RECTS: Record<IdCaptureMode, GuideRect> = {
  // Landscape card, centred; on-screen aspect (0.86/0.655)*(4/3) ≈ 1.75.
  document: {
    x: 0.07, y: 0.1725, width: 0.86, height: 0.655,
  },
  // Upright portrait, centred; on-screen aspect (0.474/0.84)*(4/3) ≈ 0.75 (3:4).
  selfie: {
    x: 0.263, y: 0.08, width: 0.474, height: 0.84,
  },
};

/** Alignment guides drawn over the camera preview (one outline per capture step). */
export function IdCaptureGuideOverlays({ mode }: IdCaptureGuideOverlaysProps) {
  const rect = ID_CAPTURE_GUIDE_RECTS[mode];
  return (
    <Box
      testID={mode === 'selfie' ? 'id-capture-face-overlay' : 'id-capture-card-overlay'}
      position="absolute"
      left={`${rect.x * 100}%`}
      top={`${rect.y * 100}%`}
      width={`${rect.width * 100}%`}
      height={`${rect.height * 100}%`}
      borderWidth={3}
      borderColor="$borderFocus"
      borderRadius={mode === 'selfie' ? '$full' : '$md'}
      opacity={0.9}
      pointerEvents="none"
    />
  );
}
